import { saveOnboardingComplete, type OnboardingCompletePayload } from './onboardingCompleteSave';
import type { SaveFailure } from './onboardingSaveAttempt';
import { applySuccessfulSave, getStoredAuth } from './onboardingSaveStorage';

/** True for failures the user can safely continue past while the save retries in the background (#11472). */
export function isRecoverableSaveFailure(failure: SaveFailure): boolean {
  return failure.reason === 'network' || failure.reason === 'timeout' || failure.reason === 'server';
}

/**
 * A finished setup whose save to the cloud hasn't landed yet (#11472).
 * The user keeps going into the app; the save is retried in the background
 * until the server confirms it, so a network hiccup never sends them back
 * to the start of setup.
 */
export const PENDING_ONBOARDING_SAVE_KEY = 'maestro_onboarding_pending_save';

type PendingRecord = { userId: string | null; payload: OnboardingCompletePayload; at: number };

export function storePendingOnboardingSave(userId: string | null | undefined, payload: OnboardingCompletePayload): void {
  try {
    const record: PendingRecord = { userId: userId || null, payload, at: Date.now() };
    localStorage.setItem(PENDING_ONBOARDING_SAVE_KEY, JSON.stringify(record));
  } catch {
    /* ignore */
  }
}

export function clearPendingOnboardingSave(): void {
  try {
    localStorage.removeItem(PENDING_ONBOARDING_SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** The pending save for this user, or null. A record left by another account is dropped. */
export function getPendingOnboardingSave(userId: string | null | undefined): OnboardingCompletePayload | null {
  let record: PendingRecord | null = null;
  try {
    const raw = localStorage.getItem(PENDING_ONBOARDING_SAVE_KEY);
    record = raw ? (JSON.parse(raw) as PendingRecord) : null;
  } catch {
    record = null;
  }
  if (!record || typeof record !== 'object' || !record.payload) return null;
  if (record.userId && userId && record.userId !== userId) {
    clearPendingOnboardingSave();
    return null;
  }
  return record.payload;
}

/**
 * Leaving the "You're all set" screen. A network/server save failure never traps the
 * user: they go into the app and the save is kept pending for background retry (#11472).
 */
export function finishOnboarding(failure: SaveFailure | null, payload: OnboardingCompletePayload, onDone: () => void): void {
  if (failure && isRecoverableSaveFailure(failure)) {
    storePendingOnboardingSave(getStoredAuth()?.user?.id, payload);
    applySuccessfulSave(payload.genres);
  }
  onDone();
}

type FlushDeps = {
  getAccessToken: () => string | null | undefined;
  refreshAccessToken?: () => Promise<string | null>;
  fetchFn?: typeof fetch;
  cloudApiUrl?: string;
};

/** One background attempt. True once the server has the save (pending record cleared). */
export async function flushPendingOnboardingSave(userId: string | null | undefined, deps: FlushDeps): Promise<boolean> {
  const payload = getPendingOnboardingSave(userId);
  if (!payload) return true;
  const result = await saveOnboardingComplete(payload, { ...deps, sleep: async () => {} });
  if (result.ok) {
    clearPendingOnboardingSave();
    console.info('[Onboarding] background save ok');
    return true;
  }
  console.warn(`[Onboarding] background save failed reason=${result.reason} status=${result.status ?? 'none'}`);
  return false;
}

type RetryOptions = {
  intervalMs?: number;
  maxAttempts?: number;
  setTimeoutFn?: (fn: () => void, ms: number) => unknown;
  clearTimeoutFn?: (handle: unknown) => void;
};

/** Retry the pending save now, then on a timer and whenever the browser comes back online. Returns a stop function. */
export function startPendingOnboardingSaveRetry(
  userId: string | null | undefined,
  deps: FlushDeps,
  opts: RetryOptions = {},
): () => void {
  const intervalMs = opts.intervalMs ?? 60_000;
  const maxAttempts = opts.maxAttempts ?? 30;
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms));
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  let stopped = false;
  let running = false;
  let attempts = 0;
  let timer: unknown = null;

  const onOnline = () => { void attempt(); };
  const stop = () => {
    stopped = true;
    if (timer !== null) clearT(timer);
    timer = null;
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
  };

  async function attempt(): Promise<void> {
    if (stopped || running) return;
    running = true;
    attempts += 1;
    let done = false;
    try {
      done = await flushPendingOnboardingSave(userId, deps);
    } catch {
      done = false;
    }
    running = false;
    if (stopped) return;
    if (done || attempts >= maxAttempts) {
      stop();
      return;
    }
    if (timer !== null) clearT(timer);
    timer = setT(() => { timer = null; void attempt(); }, intervalMs);
  }

  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
  void attempt();
  return stop;
}
